import { Component } from '@theme/component';
import { VideoBackgroundComponent } from '@theme/video-background';

const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
const mobile = matchMedia('(max-width: 749px)');

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} [videoSources]
 * @property {HTMLVideoElement} [videoElement]
 * @property {HTMLElement} [poster]
 * @property {HTMLVideoElement} [posterFrame]
 * @property {HTMLElement} [playButton]
 * @property {HTMLTemplateElement} [videoTemplate]
 * @extends VideoBackgroundComponent
 */
export class FroojiVideo extends VideoBackgroundComponent {
  requiredRefs = [];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {IntersectionObserver | undefined} */
  #preloader;

  connectedCallback() {
    Component.prototype.connectedCallback.call(this);

    const mode = this.getAttribute('loading') ?? '';
    (this.#modes[mode] ?? this.#modes.idle)();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#preloader?.disconnect();
  }

  #modes = {
    eager: () => {
      if (reducedMotion.matches) return;

      const start = () => {
        this.#loadSources();
        this.#play();
      };

      if (mobile.matches) this.#whenIdle(start);
      else start();
    },

    idle: () => {
      if (reducedMotion.matches) return;

      this.#whenIdle(() => this.#observeViewport());
    },

    click: () => {
      this.#whenIdle(() => this.#preloadWhenNear());
      this.#pauseOffscreen();
    },
  };

  #preloadWhenNear() {
    this.#preloader = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        this.#preloader?.disconnect();
        this.#showPosterFrame();
        this.#materialize();
      },
      { rootMargin: '300px' }
    );

    this.#preloader.observe(this);
  }

  #showPosterFrame() {
    const { posterFrame } = this.refs;

    if (!posterFrame) return;

    posterFrame.preload = 'metadata';
    posterFrame.load();
  }

  #materialize() {
    const { videoTemplate } = this.refs;

    if (!videoTemplate) return;

    this.append(videoTemplate.content.cloneNode(true));
    videoTemplate.remove();
    this.querySelector('video')?.setAttribute('preload', 'auto');
    this.#loadSources();
  }

  #pauseOffscreen() {
    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) this.refs.videoElement?.pause();
    });

    this.#observer.observe(this);
  }

  playVideo() {
    this.#materialize();

    const video = this.refs.videoElement;

    if (!video) return;

    video.muted = false;

    if (video.paused) this.#play(video);
    else video.pause();
  }

  #loadSources() {
    if (!this.querySelector('[ref="videoSources[]"]')) return;

    for (const source of this.querySelectorAll('[data-video-media]')) {
      const media = /** @type {HTMLElement} */ (source).dataset.videoMedia;

      if (media && !matchMedia(media).matches) source.remove();
    }

    super.connectedCallback();

    const video = this.refs.videoElement;

    video?.addEventListener('playing', () => this.#hidePoster(), { once: true });
    video?.addEventListener('play', () => this.#setPlaying(true));
    video?.addEventListener('pause', () => this.#setPlaying(false));
  }

  /** @param {boolean} playing */
  #setPlaying(playing) {
    this.toggleAttribute('playing', playing);

    const { playButton } = this.refs;
    const label = playing ? playButton?.dataset.labelPause : playButton?.dataset.labelPlay;

    if (label) playButton?.setAttribute('aria-label', label);
  }

  /** @param {HTMLVideoElement} [video] */
  #play(video = this.refs.videoElement) {
    video?.play?.()?.catch(() => this.refs.playButton?.removeAttribute('hidden'));
  }

  #hidePoster() {
    this.refs.poster?.style.setProperty('opacity', '0');
  }

  /** @param {() => void} callback */
  #whenIdle(callback) {
    const idle = () => window.requestIdleCallback?.(callback, { timeout: 1500 }) ?? setTimeout(callback, 1500);

    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }

  #observeViewport() {
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#loadSources();
            this.#play();
          } else {
            this.refs.videoElement?.pause();
          }
        }
      },
      { rootMargin: '300px 0px' }
    );

    this.#observer.observe(this);
  }
}

if (!customElements.get('frooji-video')) {
  customElements.define('frooji-video', FroojiVideo);
}
